import React from "react";

import { useTurnstile, Turnstile } from "react-turnstile";

import { type VerificationService } from "@/lib/types/types";

import { useFlag } from "@/components/FeatureFlags/hooks";
import { MetricIds, useMetrics } from "@/components/FeatureFlags/useMetrics";

import { getQueryOrOption } from "@/lib/ServerApi/TestingRouteOverrides";

import { QUERY_STRING_CAPTCHA_ERROR_OVERRIDE } from "@/constants";

import { CaptchaError } from "@/lib/types/runtimeTypes";

import { isCaptchaEnabled } from "../../../lib/ProgramTheme/programThemeGetters";

import { CaptchaErrorComponent, type CaptchaErrorType } from "./CaptchaErrorComponent";

export interface CaptchaComponentProps {
  verificationService: VerificationService;
  onChange: (value: string) => void;
  isErrored: boolean;
}

const CAPTCHA_RETRY_INTERVAL = 120000; // 2 minutes

const captchaErrorMetricMap = {
  [CaptchaError.CLIENT]: MetricIds.CaptchaErrorClient,
  [CaptchaError.SERVER]: MetricIds.CaptchaErrorServer,
  [CaptchaError.BROWSER_COMPATIBILITY]: MetricIds.CaptchaErrorBrowserCompatibility,
} as const;

/**
 * CloudFlare Turnstile invisible captcha. Conditionally loads the captcha based on program theme and renders any related errors.
 */
export const CaptchaComponent = ({
  verificationService,
  onChange,
  isErrored,
}: CaptchaComponentProps) => {
  const turnstile = useTurnstile();
  const captchaEnabledFlag = useFlag("captcha") === "enabled";

  const { programTheme, programId } = verificationService;
  const segment = verificationService.verificationResponse.segment;
  const { trackEvent } = useMetrics({ segment, programId });
  const captchaEnabled = isCaptchaEnabled(programTheme) || captchaEnabledFlag;

  const mockCaptchaError = getQueryOrOption(QUERY_STRING_CAPTCHA_ERROR_OVERRIDE);
  const mockError = Object.values(CaptchaError).includes(mockCaptchaError as CaptchaError)
    ? (mockCaptchaError as CaptchaError)
    : null;

  const [error, setError] = React.useState<CaptchaErrorType>(
    mockError ?? (isErrored ? CaptchaError.SERVER : null),
  );

  React.useEffect(() => {
    if (isErrored) {
      setError((prev) => prev ?? CaptchaError.SERVER);
    }
  }, [isErrored]);

  const handleRetry = () => {
    trackEvent(MetricIds.CaptchaRetryClicked);
    setError(null);
    turnstile.reset();
  };

  const handleError = (errorType: CaptchaError) => {
    trackEvent(captchaErrorMetricMap[errorType]);
    setError(errorType);
    onChange("");
  };

  if (!captchaEnabled) {
    return null;
  }

  return (
    <div>
      <Turnstile
        sitekey={import.meta.env.VITE_CAPTCHA_SITE_KEY}
        refreshExpired="auto"
        retryInterval={CAPTCHA_RETRY_INTERVAL}
        onLoad={() => {
          trackEvent(MetricIds.CaptchaRendered);
        }}
        onVerify={(token) => {
          trackEvent(MetricIds.CaptchaVerified);
          onChange(token);
        }}
        onError={() => {
          handleError(CaptchaError.CLIENT);
        }}
        onUnsupported={() => {
          handleError(CaptchaError.BROWSER_COMPATIBILITY);
        }}
      />

      <CaptchaErrorComponent errorType={error} onRetry={handleRetry} />
    </div>
  );
};
